import { useEffect, useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useUpdateArtifactDescription } from "@/hooks/useProjects";
import { isArtifactEnriching } from "@/lib/artifacts";
import type { Artifact } from "@/services/projectsApi";

interface ArtifactDescriptionEditorProps {
  artifact: Artifact;
  projectId: string;
  /** Hides the "DESCRIPTION" heading where the surrounding row already says so. */
  hideLabel?: boolean;
}

/** Read and correct an artifact's description.
 *
 *  Shared by the preview dialog and the post-extraction review list so the two
 *  cannot drift. The enrichment guard in particular has to hold in both places:
 *  the worker rewrites the description and its search index entries when it
 *  finishes, so an edit made while it runs is thrown away. The API answers 409
 *  to one; this hides the affordance so it never gets that far.
 *
 *  Saving is per-artifact and immediate rather than batched into a later
 *  confirm step — the artifact is already persisted by the time anyone reads
 *  this, and the save re-indexes, so there is nothing to be gained by holding
 *  the edit and plenty to lose if the dialog closes. */
const ArtifactDescriptionEditor = ({
  artifact,
  projectId,
  hideLabel = false,
}: ArtifactDescriptionEditorProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const updateDescription = useUpdateArtifactDescription();
  const beingDescribed = isArtifactEnriching(artifact);

  // The preview dialog reuses one instance as the user moves between artifacts,
  // so a half-finished edit must not follow them to the next one.
  useEffect(() => {
    setEditing(false);
    updateDescription.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifact.uid]);

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        {hideLabel ? (
          <span />
        ) : (
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Description
          </p>
        )}
        {!editing && !beingDescribed && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 text-muted-foreground"
            onClick={() => {
              setDraft(artifact.description ?? "");
              setEditing(true);
            }}
          >
            <Pencil className="w-3.5 h-3.5" />
            {artifact.description ? "Edit" : "Add"}
          </Button>
        )}
        {beingDescribed && (
          <span className="text-xs text-muted-foreground">Editable once this finishes</span>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={6}
            placeholder="Describe what this artefact shows."
            className="text-sm"
          />
          {/* Said plainly because it is not obvious: this text is what report
              generation retrieves, so an edit changes the report, not just the
              screen. */}
          <p className="text-xs text-muted-foreground">
            Used to find this artefact when writing the report.
          </p>
          {updateDescription.isError && (
            <p className="text-xs text-destructive">
              {updateDescription.error?.message || "Could not save the description."}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={updateDescription.isPending}
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="brand"
              size="sm"
              className="gap-1.5"
              disabled={updateDescription.isPending || draft === (artifact.description ?? "")}
              onClick={() =>
                updateDescription.mutate(
                  { projectId, uid: artifact.uid, description: draft },
                  { onSuccess: () => setEditing(false) }
                )
              }
            >
              {updateDescription.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-foreground whitespace-pre-wrap">
          {artifact.description ||
            (beingDescribed ? (
              <span className="text-muted-foreground flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Being written now — this takes a moment after extraction.
              </span>
            ) : (
              <span className="text-muted-foreground">No description yet.</span>
            ))}
        </p>
      )}
    </div>
  );
};

export default ArtifactDescriptionEditor;
